import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import RACI from "src/components/RACI";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import cardStyle from "src/components/OrgChart/components/Milestone/styles";
import { useAuth } from "src/context/Auth/context";
import LovKanban from "src/components/Lov/Kanban";
import { IKanbanType } from "src/pages/Actions/Kanban/kanbanModes/MultipleKanbans/interface";
import { IProgram, IProgramModal, IRefProgramModals } from "./interface";
import { addAndChangeSchemaProgram, deleteSchemaProgram } from "./schema";

const ModalProgram: React.ForwardRefRenderFunction<IRefProgramModals, IProgramModal> = ({
	onCallbackAddProgram,
	onCallbackProgram
}, ref) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { user } = useAuth();
	
	const [programSelected, setProgramSelected] = useState<IProgram>();

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		unregister: unregisterAddOrChange,
		control: controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange 
	} = useForm();

	const submitAddOrChange = async (submitForm: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			const stringToNumberOrNull = (obj: any) => {
				if(typeof obj === "string" && obj === "")
					return null;
				if(typeof obj === "string")
					return parseInt(obj, 10);
				if(typeof obj !== "number")
					return null;
				return obj;
			};
  

			const dataForm = {
				...submitForm,
				pra001Kba001Id: stringToNumberOrNull(submitForm.pra001Kba001Id),
				prtba012: submitForm.prtba012?.filter((x: any) => x.pra012Id).map((participant: any) => ({
					...participant,
					pra012Id: Number(participant.pra012Id),
					pra012Efa004Id: typeof participant.pra012Efa004Id === "object" ? Number(participant.pra012Efa004Id.value) : Number(participant.pra012Efa004Id) || null,
					pra012Efa005Id: typeof participant.pra012Efa005Id === "object" ? Number(participant.pra012Efa005Id.value) : Number(participant.pra012Efa005Id) || null
				})).filter((e: any) => e.pra012Efa004Id || e.pra012Efa005Id)
			};

			await addAndChangeSchemaProgram(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if(isAdd){
				const {data} = await RCCServicesApi.postRequest({
					method: "Program/Insert",
					body: {
						...dataForm,
						pra001Id: 0		
					}
				});

				onCallbackAddProgram({
					...dataForm,
					pra001Id: data,
					permission: 2
				});

			}else{
				await RCCServicesApi.putRequest({
					method: "Program/Update",
					body: {
						...dataForm
					}
				});

				onCallbackProgram(dataForm.pra001Id);
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Programa adicionado com sucesso") : translate("Programa alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar programa") : translate("Erro ao alterar programa"), addToast);
		}
	};

	const actionButtonAdd = () => {
		setProgramSelected(undefined);
		resetAddOrChange(); 
		setIsAdd(true);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (id: number) => {
		resetAddOrChange(); 
		setIsAdd(false);
		getProgram(id);
	};

	async function getProgram(id: number){
		const result = await RCCServicesApi.getRequest({
			method: "Program/Select",
			urlParameters: {id}
		});
		setProgramSelected(result.data);
		modalAddOrChange.current?.openModal();
	}

	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const onCallbackDelete = useRef<Function | undefined>(undefined);
	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset: resetDelete, 
		setError: setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete
	} = useForm(); 

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchemaProgram(translate).validate({...data, "pra001TituloConfirmation": programSelected?.pra001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "Program/Delete",
				urlParameters: {
					id: programSelected?.pra001Id
				}
			});

			addToast({
				color: "green",
				description: translate("Programa excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete.current !== undefined)
				onCallbackDelete.current(programSelected);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir programa"), addToast);
		}
		
	};

	const actionButtonDelete = (program: IProgram, onCallback?: Function) => {
		resetDelete();
		setProgramSelected(program);
		onCallbackDelete.current = onCallback;
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	return(
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão do Programa")}
				description= {`${translate("Deseja realmente excluir o programa")}: ${programSelected?.pra001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="pra001Titulo"
						label={translate("Digite o título do programa para excluí-lo")}
						maxLength={200}
						error={errorsDelete.pra001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Programa") : translate("Alterar Programa"),
					icon: {
						name: isAdd ? "plus" : "pencil" 
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						alignItems="center"
						justifyContent="flex-end"
					>
						{
							programSelected && <Audit data={programSelected} type="pop"/>
						}
					</RCCGrid>
					<RCCGrid styleInline="display: none">
						<RCCInput 
							register={registerAddOrChange}
							name="pra001Id"
							label=""
							defaultValue={programSelected?.pra001Id || 0}
							readOnly
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
					>
						<RCCInput 
							register={registerAddOrChange}
							name="pra001Titulo"
							label={translate("Título")}
							maxLength={200}
							defaultValue={programSelected?.pra001Titulo}
							error={errorsAddOrChange.pra001Titulo}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
					>
						<LovKanban
							control={controlAddOrChange}
							name="pra001Kba001Id"
							label={translate("Kanban padrão")}
							defaultValue={programSelected?.pra001Kba001Id ? programSelected?.pra001Kba001Id.toString() : undefined}
							isClearable
							error={errorsAddOrChange.pra001Kba001Id}
							extraParametersOnLoad={{
								type: IKanbanType.PROJECT
							}}
							isCreatable
							extraParametersOnCreate={{
								type: IKanbanType.PROJECT
							}}
						/> 
					</RCCGrid>
					{/* RACI */}
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
						styleInline={cardStyle}
					>
						<RCCGrid
							container
							item
							alignItems="flex-end"
						>
							<Title 
								color="purple"
								marginBottom={15}
							>
								{translate("RACI")}
							</Title>
						</RCCGrid>
						<RACI 
							noCheckbox
							responsible={{
								nameOfSelect: "pra001Efa005Id",
								defaultValue: {
									select: isAdd ? `${user.efa005Id}` : `${programSelected?.pra001Efa005Id}`,
									checkbox: "N"
								}
							}}
							accountable={{
								nameOfSelect: "pra001Efa005Id2",
								defaultValue: {
									select: isAdd ? `${user.efa005Id}` : `${programSelected?.pra001Efa005Id2}`,
									checkbox: "N"
								}
							}}
							control={controlAddOrChange}
							register={registerAddOrChange}
							unregister={unregisterAddOrChange}
							clearErrors={clearErrorsAddOrChange}
							error={errorsAddOrChange}
							participants={{
								nameOfObject: "prtba012",
								nameOfID: "pra012Id",
								nameOfCheckbox: "",
								nameOfSelectGroup: "pra012Efa004Id",
								nameOfSelectGroupDescription: "pra012Efa004Nome",
								nameOfSelectUser: "pra012Efa005Id",
								nameOfSelectUserDescription: "pra012Efa005Nome",
								nameOfType: "pra012Tipo",
								defaultValue: programSelected?.prtba012 ? programSelected?.prtba012 : undefined
							}}	
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
		
	);
};

export default forwardRef(ModalProgram);
